import React, { useState } from "react";
import { api } from "@/lib/api";
import { usePaywallGuard } from "@/lib/usePaywallGuard";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import DatePicker from "@/components/DatePicker";
import PlaceInput from "@/components/PlaceInput";
import { TrendingUp, Loader2, ChevronRight } from "lucide-react";

/**
 * Transits — shows current Saturn/Jupiter/Mars sidereal positions and their
 * impact on the user's natal chart over the next 12 months.
 *
 * Natal data is REQUIRED (needs Moon + Ascendant). Ad-gated 1 action.
 */
export default function Transits() {
  const guard = usePaywallGuard();
  const [form, setForm] = useState({ dob: "", time: "12:00", name: "" });
  const [city, setCity] = useState(null);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const run = async (e) => {
    e.preventDefault();
    setError("");
    if (!city) { setError("Please select your birth city"); return; }
    setLoading(true);
    try {
      const body = { dob: form.dob, time: form.time, lat: city.lat, lon: city.lon, tz: city.tz, name: form.name || null };
      const { data: res } = await guard(
        async () => await api.post("/astro/transits", body),
        "Transit Predictions",
      );
      setData(res);
    } catch (err) {
      setError(err?.response?.data?.detail || err.message || "Failed to fetch transits");
    } finally {
      setLoading(false);
    }
  };

  const toneClass = (tone) => tone === "Favourable" ? "text-emerald-300 bg-emerald-500/10 border-emerald-400/30"
    : tone === "Challenging" ? "text-rose-300 bg-rose-500/10 border-rose-400/30"
    : "text-amber-300 bg-amber-500/10 border-amber-400/30";

  return (
    <div className="space-y-6" data-testid="transits-page">
      <header>
        <h1 className="font-serif text-4xl text-slate-50 mb-2">Transit Predictions</h1>
        <p className="text-slate-400">Current Saturn, Jupiter & Mars positions and their month-by-month impact on your natal chart.</p>
      </header>

      <Card className="glass border-white/10 p-6">
        <form onSubmit={run} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="transits-form">
          <div>
            <Label className="text-slate-300">Full Name <span className="text-slate-500 text-xs">(optional)</span></Label>
            <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="transits-name" className="bg-slate-900/50 border-white/10 text-white" />
          </div>
          <div>
            <Label className="text-slate-300">Date of Birth</Label>
            <DatePicker value={form.dob} onChange={(v) => setForm({ ...form, dob: v })} required />
          </div>
          <div>
            <Label className="text-slate-300">Birth Time</Label>
            <Input type="time" value={form.time} onChange={(e) => setForm({ ...form, time: e.target.value })} required data-testid="transits-time" className="bg-slate-900/50 border-white/10 text-white" />
          </div>
          <div>
            <Label className="text-slate-300">Birth Place</Label>
            <PlaceInput value={city} onChange={setCity} data-testid="transits-place" />
          </div>
          <div className="md:col-span-2 lg:col-span-4">
            <Button type="submit" disabled={loading} data-testid="transits-submit" className="bg-amber-500 hover:bg-amber-600 text-slate-950 rounded-full font-medium px-8">
              {loading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Reading the sky...</> : <>Reveal My Transits <ChevronRight className="w-4 h-4 ml-1" /></>}
            </Button>
            {error && <span className="ml-3 text-sm text-rose-400" data-testid="transits-error">{error}</span>}
          </div>
        </form>
      </Card>

      {data && (
        <>
          <Card className="glass border-white/10 p-6" data-testid="transits-current">
            <div className="flex items-center gap-2 mb-4">
              <TrendingUp className="w-5 h-5 text-gold" />
              <h3 className="font-serif text-2xl text-slate-50">Current Sky</h3>
              <span className="ml-2 text-xs text-slate-500">Lagna: {data.ascendant_sign} · Moon: {data.moon_sign}</span>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {data.planets.map((p) => (
                <div key={p.planet} className="p-4 rounded-xl bg-slate-900/50 border border-white/5" data-testid={`transit-${p.planet.toLowerCase()}`}>
                  <div className="flex items-baseline justify-between">
                    <div className="font-serif text-xl text-slate-50">{p.planet}</div>
                    <div className="text-sm text-amber-300">{p.sign} {p.degree}°</div>
                  </div>
                  <div className="text-xs text-slate-500 mt-1">Exits sign on <span className="text-slate-300">{p.exits_sign_on}</span></div>
                  <div className={`mt-3 p-2 rounded-lg border text-xs ${toneClass(p.from_ascendant.tone)}`}>
                    <div className="font-medium">From Lagna · {p.from_ascendant.tone}</div>
                    <div className="mt-0.5 opacity-90 leading-relaxed">{p.from_ascendant.detail}</div>
                  </div>
                  <div className={`mt-2 p-2 rounded-lg border text-xs ${toneClass(p.from_moon.tone)}`}>
                    <div className="font-medium">From Moon · {p.from_moon.tone}</div>
                    <div className="mt-0.5 opacity-90 leading-relaxed">{p.from_moon.detail}</div>
                  </div>
                </div>
              ))}
            </div>
          </Card>

          <Card className="glass border-white/10 p-6" data-testid="transits-timeline">
            <h3 className="font-serif text-2xl text-slate-50 mb-4">12-Month Timeline (from your Moon)</h3>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-xs uppercase tracking-wider text-amber-300 border-b border-white/5">
                    <th className="text-left py-2 pr-3">Month</th>
                    <th className="text-left py-2 pr-3">Saturn</th>
                    <th className="text-left py-2 pr-3">Jupiter</th>
                    <th className="text-left py-2">Mars</th>
                  </tr>
                </thead>
                <tbody>
                  {data.monthly_timeline.map((row) => (
                    <tr key={row.month} className="border-b border-white/5" data-testid={`timeline-row-${row.month.replace(' ', '-')}`}>
                      <td className="py-2 pr-3 text-slate-200">{row.month}</td>
                      {row.transits.map((t) => (
                        <td key={t.planet} className="py-2 pr-3">
                          <span className={`inline-block px-2 py-0.5 rounded-full text-xs mr-1 ${
                            t.tone === "Favourable" ? "bg-emerald-500/15 text-emerald-300"
                            : t.tone === "Challenging" ? "bg-rose-500/15 text-rose-300"
                            : "bg-amber-500/15 text-amber-300"
                          }`}>{t.sign} · H{t.house_from_moon}</span>
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="text-xs text-slate-500 mt-3">
              <span className="text-emerald-300">Green</span> = favourable from your natal Moon ·
              <span className="text-rose-300 ml-2">Red</span> = challenging ·
              <span className="text-amber-300 ml-2">Amber</span> = mixed
            </p>
          </Card>
        </>
      )}
    </div>
  );
}
